import { useRef, useState, type KeyboardEvent } from "react";
import { CornerDownLeft, X } from "lucide-react";
import { AppButton, isEnterSubmit } from "@libs/zapi-ui/custom";
import { useTranslation } from "react-i18next";

type TProps = {
  options: string[];
  onChange: (next: string[]) => void;
};

export function OptionsTagInput({ options, onChange }: TProps) {
  const { t } = useTranslation("projects");
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = () => {
    const value = draft.trim();
    if (!value) return;
    if (options.includes(value)) {
      setDraft("");
      return;
    }
    onChange([...options, value]);
    setDraft("");
    inputRef.current?.focus();
  };

  const remove = (val: string) => {
    onChange(options.filter((o) => o !== val));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isEnterSubmit(e, { allowShift: true })) {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && draft === "" && options.length > 0) {
      onChange(options.slice(0, -1));
    }
  };

  return (
    <div className="flex w-full items-center gap-1 rounded-lg border border-[#ececee] bg-white p-1">
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {options.map((opt) => (
          <span
            key={opt}
            className="inline-flex h-6 items-center gap-1 rounded-md bg-innoveraZinc-150 px-2 text-[12px] font-medium leading-[11px] text-innoveraZinc-900"
          >
            {opt}
            <button
              type="button"
              className="text-innoveraZinc-900 hover:opacity-70"
              onClick={() => remove(opt)}
              aria-label={t("dimensions.removeOption", { option: opt })}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("dimensions.optionPlaceholder")}
          className="h-6 min-w-[80px] flex-1 border-0 bg-transparent px-1 text-[14px] text-zinc-900 placeholder:text-[#a1a1aa] focus:outline-none focus:ring-0"
        />
      </div>
      <AppButton
        size="sm"
        appearance="contained"
        color="zinc950"
        className="shrink-0 text-[14px] leading-[13px]"
        onClick={commit}
        disabled={!draft.trim()}
      >
        {t("dimensions.addOption")}
        <CornerDownLeft className="h-3 w-3" />
      </AppButton>
    </div>
  );
}
